import { useCallback, useState } from 'react';
import { ModalsKeys, Nullable } from 'global';
import { useMutation, useQuery } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { FilterTableType, TableFilter } from '@trnsact/trnsact-shared-types';

import { opportunitiesListApi, usePresets } from '../api';
import { GetTableFiltersResponse, GetTableFiltersVariables } from 'modules/tableFilter';
import { formatTableFilterFromResponse } from '../lib';
import { VOFilters } from 'modules/vendorOpportunity';
import { useModal } from 'hooks/useModal';

export function useOpportunitiesListFilters(onFilterDeleteCallback: (tf: TableFilter) => void) {
  const { handleOpen: handleConfirmationOpen, handleClose: handleConfirmationClose } = useModal(
    ModalsKeys.Confirmation
  );

  const { loading: isTableFiltersLoading } = useQuery<GetTableFiltersResponse, GetTableFiltersVariables>(
    opportunitiesListApi.getTableFilters,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      variables: { tableType: FilterTableType.Opportunities },
      onCompleted(data) {
        const tableFilters = data?.tableFilter?.tableFilters?.data
          ? data.tableFilter.tableFilters.data.map(formatTableFilterFromResponse)
          : [];
        const defaultFilter = tableFilters.find(tf => tf.isDefault) ?? null;
        setTableFilters(tableFilters);
        setDefaultFilter(defaultFilter);
        setIsFiltersDataLoading(false);
      },
    }
  );

  const [toggleDefaultTableFilter, { loading: isSetDefaultTableFilterLoading }] = useMutation(
    opportunitiesListApi.toggleDefaultTableFilter
  );

  const { showNotification } = useNotifications();
  const { handleDeleteFilter } = usePresets();

  const [isFiltersDataLoading, setIsFiltersDataLoading] = useState<boolean>(true);
  const [isFilterDeleting, setIsFilterDeleting] = useState<boolean>(false);
  const [tableFilters, setTableFilters] = useState<TableFilter[]>([]);
  const [defaultFilter, setDefaultFilter] = useState<Nullable<TableFilter>>();
  const [currentFilterConfig, setCurrentFilterConfig] = useState<Nullable<Partial<VOFilters>>>(null);

  const handleSetDefaultFilter = useCallback(
    async (tableFilterId: string) => {
      const result = await toggleDefaultTableFilter({
        variables: {
          tableFilterId,
        },
      });
      if (result?.data?.tableFilter?.toggleDefaultTableFilter?.success) {
        const updatedTableFilter = result?.data?.tableFilter?.toggleDefaultTableFilter?.entity;
        setTableFilters(prev =>
          prev.map(tf => (tf.tableFilterId === tableFilterId ? updatedTableFilter : { ...tf, isDefault: false }))
        );
        setDefaultFilter(updatedTableFilter.isDefault ? updatedTableFilter : null);
        showNotification('Changes Saved');
      } else {
        showNotification('Error on saving', { type: 'error' });
      }
    },
    [showNotification, toggleDefaultTableFilter]
  );

  const handleFilterDelete = (tf: TableFilter) => {
    handleConfirmationOpen({
      title: 'Delete filter',
      message: `Are you sure you want to delete ${tf.name} filter?`,
      onConfirm: async () => {
        setIsFilterDeleting(true);
        const result = await handleDeleteFilter(tf.tableFilterId);
        if (result?.success) {
          setTableFilters(prev => prev.filter(({ tableFilterId }) => tf.tableFilterId !== tableFilterId));
          setDefaultFilter(defaultFilter?.tableFilterId === tf.tableFilterId ? null : defaultFilter);
          handleConfirmationClose();
          onFilterDeleteCallback(tf);
          setIsFilterDeleting(false);
        }
      },
    });
  };

  return {
    defaultFilter,
    tableFilters,
    currentFilterConfig,
    setCurrentFilterConfig,
    isTableFiltersLoading,
    handleSetDefaultFilter,
    handleFilterDelete,
    isFilterDeleting,
    isFiltersDataLoading,
    isSetDefaultTableFilterLoading,
  };
}
